import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { getSpecialOffers } from "~/core/deprecated/special-offers";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import {
  MainBanner,
  MainBannerTranslationProvider,
} from "~/features/main-banner";
import { PromoPageSteps } from "~/features-deprecated/promoPageSteps/PromoPageSteps";
import { SpecialOffers } from "~/features-deprecated/special-offers/SpecialOffers";
import { WelcomeOffer } from "~/features-deprecated/welcome-offer/WelcomeOffer";
import { Club, Gift, UserCircle } from "~/icons";
import { Background, StripeWrapper } from "~/shared/ui-kit";
import { getAffCookie, makePlayNowUrl } from "~/shared/utils/aff-integration";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPageConstructor } from "~/widgets/page-constructor/page-constructor.server";
import { SupportMedia } from "~/widgets/support/SupportMedia";
import { SupportMessengers } from "~/widgets/support/SupportMessengers";

import type { Route } from "./+types/$lang._index";

const createHomeValidator = (): ObjectSchema<{
  lang: string;
}> => {
  return object({
    lang: string().required(),
  });
};

export const loader = (async ({
  context: { featureFlags, settings, sdk },
  params,
  request,
}) => {
  const { lang } = createHomeValidator().validateSync(params);
  const homeSlug =
    typeof settings?.app?.home?.slug === "string"
      ? settings.app.home.slug
      : undefined;
  const isHomeConstructorPageEnabled = featureFlags.getItem(
    "homePageConstructor",
  ).enabled;

  if (isHomeConstructorPageEnabled && !homeSlug) {
    throw new Response(
      JSON.stringify({
        message: "Home page not found",
      }),
      {
        status: 404,
        statusText: "Not Found",
      },
    );
  }

  const [affiliate, pageConstructorData] = await Promise.all([
    getAffCookie(request),
    homeSlug
      ? loadPageConstructor(
          {
            sdk,
            slug: homeSlug,
            segment: settings?.app?.home?.segment,
            lang,
          },
          "light",
        )
      : undefined,
  ]);

  return responseData(
    {
      specialOffers: getSpecialOffers(),
      affiliate,
      featureFlags: {
        isSpecialOffersButtonEnabled: featureFlags.getItem(
          "specialOffersButton",
        ).enabled,
        isSupportIconsEnabled:
          featureFlags.getItem("supportIconsBlock").enabled,
        isSocialMediaIconsEnabled:
          featureFlags.getItem("socialMediaIcons").enabled,
      },
      pageConstructorData,
      settings,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      isHomeConstructorPageEnabled,
    },
    {
      headers: {
        "Cache-Control": "public, max-age=300",
      },
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  return prepareSeoData({
    pageSeo: data?.pageConstructorData?.page.seo,
    fallbackSeo: data?.pageConstructorData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
  });
};

const Page: FC<Route.ComponentProps> = function ({
  loaderData: {
    specialOffers,
    featureFlags,
    affiliate,
    pageConstructorData,
    isHomeConstructorPageEnabled,
  },
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation("home");
  const { t: mainBannerTranslation } = useTranslation("mainBanner");

  const welcomeOfferBonuses = [
    {
      title: t("welcome-offer.first-bonus.title"),
      subtitle: t("welcome-offer.first-bonus.subtitle"),
    },
    {
      title: t("welcome-offer.second-bonus.title"),
      subtitle: t("welcome-offer.second-bonus.subtitle"),
    },
  ];

  const data = [
    {
      icon: <Gift viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />,
      text: {
        en: "Instantly available bonuses",
        zh: "扑克界最优的欢迎优惠！",
      },
    },
    {
      icon: <Club viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />,
      text: {
        en: "Advanced protection",
        zh: "采用最先进的保护措施，\n" + "确保游戏公平公正！",
      },
    },
    {
      icon: (
        <UserCircle viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
      ),
      text: {
        en: "Superfast registration",
        zh: "超快速注册，\n" + "立即开始游戏！",
      },
    },
  ];

  const { gtm } = useGtmContext();

  return (
    <>
      {pageConstructorData && isHomeConstructorPageEnabled ? (
        <PageConstructor data={pageConstructorData} />
      ) : (
        <div className="main-page">
          <MainBannerTranslationProvider
            value={{
              heading: {
                title: mainBannerTranslation("topper.heading.title"),
              },
              buttons: {
                create: mainBannerTranslation("topper.button.create"),
                login: mainBannerTranslation("topper.button.login"),
              },
            }}
          >
            <Background
              bgValue={"/main-page-bg-mobile.jpg"}
              mdBgValue={"/main-page-bg-desktop.jpg"}
            >
              <MainBanner
                data={data}
                playNowLink={makePlayNowUrl(affiliate)}
                language={language}
                analytics={{
                  createAccountMain() {
                    gtm.createAccountMain({
                      eventTime: new Date(),
                      playerID: null,
                      screenName: "mainPage",
                    });
                  },
                  logInMain() {
                    gtm.logInMain({
                      eventTime: new Date(),
                      playerID: null,
                      screenName: "mainPage",
                    });
                  },
                }}
              />
            </Background>
          </MainBannerTranslationProvider>

          <StripeWrapper variant="default">
            <PromoPageSteps
              firstStepText={t("steps.text.registration")}
              secondStepText={t("steps.text.game")}
              thirdStepText={t("steps.text.rewards")}
              playNowText={t("steps.button.play")}
              playNowLink={makePlayNowUrl(affiliate)}
              analytics={{
                playNow() {
                  gtm.playNow({
                    eventTime: new Date(),
                    playerID: null,
                    screenName: "mainPage",
                  });
                },
              }}
            />
          </StripeWrapper>
          <WelcomeOffer
            title={t("welcome-offer.heading.title")}
            guaranteed={{
              cards: welcomeOfferBonuses,
            }}
            surpriseBonusCard={{
              title: t("welcome-offer.third-bonus.title"),
              description: t("welcome-offer.third-bonus.subtitle"),
            }}
            image={{
              imageVariants: "/happy-man-icon.png",
            }}
            background={{
              imageVariants: "/welcome-offer-bg-desktop.jpg",
              mobileImageVariants: "/welcome-offer-bg-mobile.jpg",
            }}
          />
          <SpecialOffers
            featureFlags={featureFlags}
            specialOffers={specialOffers}
          />
          {featureFlags.isSupportIconsEnabled && <SupportMessengers />}
          {featureFlags.isSocialMediaIconsEnabled && <SupportMedia />}
        </div>
      )}
    </>
  );
};

export default Page;
